import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

/* HostFix Theme System
   ---------------------
   3 stati possibili:
     - 'light'          → tema chiaro (default classico)
     - 'dark'           → tema scuro (deprecato, mantenuto per compat)
     - 'data-intel'     → tema Data Intel (Proposta C) — attivato per utente dal backend (user.ui_theme)

   Priorità di applicazione:
     1. Se user è loggato e ha ui_theme='data-intel' → applica 'data-intel' (override totale)
     2. Altrimenti rispetta la preferenza locale ('light' / 'dark') salvata
*/

const ThemeContext = createContext({
  theme: 'light',
  toggleTheme: () => {},
  cycleTheme: () => {},
  setTheme: () => {},
  applyUserTheme: () => {},
});

const STORAGE_KEY = 'hostfix-theme';
const CYCLE = ['light', 'dark', 'data-intel'];

const getInitialTheme = () => {
  if (typeof window === 'undefined') return 'light';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'data-intel') return stored;
  // Fallback su preferenza sistema
  if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'dark';
  return 'light';
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(getInitialTheme);

  useEffect(() => {
    const root = document.documentElement;
    // Pulisci tutte le classi tema possibili
    root.classList.remove('dark', 'theme-data-intel');
    if (theme === 'dark') root.classList.add('dark');
    if (theme === 'data-intel') root.classList.add('theme-data-intel');
    try { window.localStorage.setItem(STORAGE_KEY, theme); } catch (e) { /* noop */ }
  }, [theme]);

  const setTheme = useCallback((t) => setThemeState(t), []);
  const toggleTheme = useCallback(() => setThemeState((t) => (t === 'dark' ? 'light' : 'dark')), []);
  // Applica il tema dell'utente dopo il login.
  // Se user.ui_theme === 'data-intel' attiva il tema C globalmente;
  // altrimenti torna allo stato 'light' (default) ignorando i precedenti override.
  // NOTA: rispetta un eventuale override manuale dell'utente (cycleTheme)
  //       memorizzato in sessionStorage, così un token-refresh silenzioso non
  //       sovrascrive la scelta esplicita appena fatta dall'utente.
  const applyUserTheme = useCallback((uiTheme) => {
    try {
      if (window.sessionStorage.getItem('hostfix-theme-user-overridden') === '1') return;
    } catch (e) { /* noop */ }
    if (uiTheme === 'data-intel') setThemeState('data-intel');
    else setThemeState('light');
  }, []);

  // Quando l'utente preme il toggle manuale, marchiamo l'override
  // per la durata della sessione, così applyUserTheme non lo sovrascrive.
  const cycleTheme = useCallback(() => {
    try { window.sessionStorage.setItem('hostfix-theme-user-overridden', '1'); } catch (e) { /* noop */ }
    setThemeState((t) => CYCLE[(CYCLE.indexOf(t) + 1) % CYCLE.length] || 'light');
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, cycleTheme, setTheme, applyUserTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
